import { useEffect, useState } from "react";
import { Loader2, Trash2, AlertCircle } from "lucide-react";
import API from "../../api/api";
import BatchActionBar from "../../components/BatchActionBar";

export default function Bookmarks() {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Multi-delete state
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");
      const { data } = await API.get("/admin/bookmarks", { params: { page, limit } });
      if (!data?.success) throw new Error(data?.message || "Failed to load bookmarks");
      setData(data.bookmarks || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load bookmarks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleToggleSelectAll = () => {
    if (selectedIds.length === data.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(data.map((b) => b._id));
    }
  };

  const handleToggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleBatchDelete = async () => {
    try {
      setIsBatchDeleting(true);
      const { data: resData } = await API.post("/admin/batch-delete", {
        entity: "bookmarks",
        ids: selectedIds,
      });
      if (resData?.success) {
        setSelectedIds([]);
        fetchData();
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Batch delete failed");
    } finally {
      setIsBatchDeleting(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this bookmark?")) return;
    try {
      await API.delete(`/admin/bookmarks/${id}`);
      setData((prev) => prev.filter((b) => b._id !== id));
      setSelectedIds((prev) => prev.filter((i) => i !== id));
      setTotal((t) => Math.max(t - 1, 0));
    } catch (err) {
      setError(err?.response?.data?.message || "Delete failed");
    }
  };

  const totalPages = Math.max(Math.ceil(total / limit), 1);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Bookmarks</h1>
          <p className="text-sm text-gray-500">All user bookmarks</p>
        </div>
        <div className="text-sm text-gray-500">{total} total</div>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-slate-700/50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3 w-12 text-center">
                  <input
                    type="checkbox"
                    checked={data.length > 0 && selectedIds.length === data.length}
                    onChange={handleToggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                  />
                </th>
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">Bookmarked</th>
                <th className="px-4 py-3 text-left">Created</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
              {loading ? (
                <tr><td colSpan={5} className="py-8 text-center text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading...</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={5} className="py-6 text-center text-gray-500">No bookmarks</td></tr>
              ) : (
                data.map((b) => {
                  const isSelected = selectedIds.includes(b._id);
                  return (
                  <tr key={b._id} className={`transition-colors ${isSelected ? "bg-pink-50/70 dark:bg-pink-500/10" : "hover:bg-gray-50 dark:hover:bg-slate-700/30"}`}>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelectRow(b._id)}
                        className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-pink-500">{b.user?.name || b.user?.username || "-"}</td>
                    <td className="px-4 py-3 text-sm text-pink-500">{b.targetUser?.name || b.targetUser?.username || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{b.createdAt ? new Date(b.createdAt).toLocaleString() : "-"}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => onDelete(b._id)}
                        className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-lg bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Floating Multi-Delete Bar */}
      <BatchActionBar
        selectedCount={selectedIds.length}
        totalCount={total}
        onClearSelection={() => setSelectedIds([])}
        onSelectAll={handleToggleSelectAll}
        onDeleteSelected={handleBatchDelete}
        isDeleting={isBatchDeleting}
        entityName="bookmarks"
      />

      <div className="flex items-center justify-between text-sm text-gray-500">
        <div>Page {page} of {totalPages}</div>
        <div className="flex gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            className="px-3 py-1 rounded border border-gray-200 dark:border-slate-700 disabled:opacity-50"
          >
            Prev
          </button>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            className="px-3 py-1 rounded border border-gray-200 dark:border-slate-700 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
